import { useState } from "react";

import { Link, useLocation } from "react-router";
import { useSidebar } from "../context/SidebarContext";
import { useEdition } from "../context/EditionContext";
import { APP_NAME } from "../constants";
import { ThemeToggleButton } from "../components/common/ThemeToggleButton";
import UserDropdown from "../components/header/UserDropdown";
import FormLinkSplitButton from "../components/crm/FormLinkSplitButton";
import KioskQrButton from "../components/crm/KioskQrButton";
import SignaturePadButton from "../components/crm/SignaturePadButton";
import HeaderDateCalc from "../components/header/HeaderDateCalc";
import GlobalSearchBar from "../components/header/GlobalSearchBar";
import ImageTextExtractor from "../components/header/ImageTextExtractor";
import PrivacyUnlockButton from "../components/header/PrivacyUnlockButton";
import RecurringTaskBanner from "../components/header/RecurringTaskBanner";
import WhatsNewButton from "../components/whats-new/WhatsNewButton";
import NewBuildHeaderButton from "../components/header/NewBuildHeaderButton";

const AppHeader: React.FC = () => {
  const [isApplicationMenuOpen, setApplicationMenuOpen] = useState(false);
  // 통합검색이 지금 넓어져 있나 — 검색칸의 폭(접힘 14rem / 펼침 flex-1)을 정하는 데만 쓴다.
  // 2026-08-28 전에는 이 값으로 옆 컴포넌트를 통째로 감췄지만, 계산기가 칩으로 접히며
  // 자리가 남아 감출 이유가 없어졌다.
  const [isSearchExpanded, setSearchExpanded] = useState(false);

  const { isMobileOpen, toggleSidebar, toggleMobileSidebar } = useSidebar();
  const { pathname } = useLocation();
  const { edition } = useEdition();
  const isCrmSection = pathname.startsWith("/crm");

  // 외부 배포용 화면에서는 띠에 **우리 내부 도구를 하나도 남기지 않는다** — 통합검색(내부
  // 데이터를 뒤진다)·개인정보 가리기(CRM 개념)·날짜 계산기·글자 뽑기·업데이트 소식(우리
  // 릴리스노트)·정기 할 일 배너가 모두 그렇다. 남기는 건 앱을 쓰는 데 필요한 최소한이다:
  // 메뉴 열기·로고·밝기 전환·계정(로그아웃).
  // ⚠️ 여기에 조건을 하나씩 더 붙이는 방식은 결국 빠뜨린다 — 헤더에 새 도구를 넣을 때는
  //    "선택 이용 매장에게도 보이나?"를 반드시 이 줄로 판단하고, 보여야 하는 것만 밖에 둔다.
  const external = edition === "partial";

  const handleToggle = () => {
    if (window.innerWidth >= 1280) {
      // xl and above: use desktop sidebar behavior
      toggleSidebar();
    } else {
      // Below xl: use mobile sidebar behavior
      toggleMobileSidebar();
    }
  };

  const toggleApplicationMenu = () => {
    setApplicationMenuOpen(!isApplicationMenuOpen);
  };

  return (
    // 맨 위 고정 띠는 **한 줄**이다. 원래는 두 줄(윗줄 계산기·버튼·계정 / 아랫줄 통합검색)이었지만
    // 전 화면에 늘 붙어 있는 띠가 두 줄을 먹는 비용이 커서 2026-08-12 한 줄로 합쳤다.
    // 2026-08-28: 그 뒤로도 1440 이하에서는 폭이 모자라 다시 두 줄이 됐다. [내부용|외부용] 탭과
    // 넓은 화면의 햄버거를 빼고, 디데이 계산기를 칩으로 접어 1280 에서도 한 줄이 된다.
    // 같은 날 **띠 아래에 따로 깔리던 노란 '오늘 할 일' 줄도 이 안으로 들어왔다** — 그래서 이
    // 컴포넌트가 그리는 세로 높이는 어떤 상태에서도 한 줄이다(자세한 계산은 아래 통합검색 주석).
    // z 는 사이드바(z-[99998]) 바로 아래 · 모달 층(z-99999 이상)보다 아래여야 한다 — 모바일에서
    // 사이드바가 이 띠를 덮는 순서는 그대로 두면서, 어떤 모달도 이 띠에 가리지 않게 한다(티켓 #156).
    <header className="sticky top-0 flex flex-col w-full bg-white border-gray-200 z-[99997] dark:border-gray-800 dark:bg-gray-900 xl:border-b">
      <div
        // xl:flex-wrap 이 안전장치다 — 넓은 화면에서는 전부 한 줄에 들어가고, 폭이 모자라면
        // 오른쪽 버튼 묶음이 잘리는 대신 아랫줄로 내려간다(잘리면 아예 누를 수가 없다).
        // 2026-08-28: 접혔을 때 왼쪽을 206px 비우던 규칙을 없앴다. 그건 좁은 띠 밖으로
        // 삐져나오던 사이드바 로고 줄을 피하려던 자리인데, 이제 사이드바가 스스로 폭을
        // 줄이고 넘치는 걸 잘라 낸다. 기본이 접힘이라 이 여백이 남으면 모든 화면에서
        // 상단 바가 통째로 오른쪽으로 밀린 채 시작한다.
        className="flex w-full flex-col items-center justify-between grow xl:flex-row xl:flex-wrap xl:px-6"
      >
        <div
          // xl:flex-1 이라야 안쪽 검색칸이 남는 폭까지 늘어난다(w-auto 면 내용만큼만 잡혀
          // 오른쪽 묶음과의 사이가 빈 채로 남는다). gap 을 xl 에서 다시 좁히는 것도 취향이
          // 아니라 계산이다 — 실측으로 4px 이 모자라 오른쪽 묶음이 아랫줄로 내려갔다.
          className="flex items-center justify-between w-full gap-2 px-3 py-3 border-b border-gray-200 dark:border-gray-800 sm:gap-4 xl:gap-2 xl:flex-1 xl:justify-normal xl:border-b-0 xl:px-0 lg:py-4"
        >
          {/* 메뉴 열기(햄버거) — 2026-08-28 부터 **좁은 화면에서만** 보인다(xl:hidden).
              넓은 화면에서는 왼쪽 메뉴가 마우스를 올리면 저절로 펼쳐지므로 이 단추가 하는 일이
              "펼친 채 고정"뿐이었고, 그 기능은 왼쪽 메뉴의 ⚙️ 설정 안으로 옮겼다.
              ⚠️ 좁은 화면에서는 지우면 안 된다 — 서랍식 메뉴를 여는 유일한 길이다. */}
          <button
            className={`items-center justify-center  w-10 h-10 text-gray-500 border-gray-200 rounded-lg z-99999 dark:border-gray-800 flex dark:text-gray-400 lg:h-11 lg:w-11 xl:hidden ${
              isMobileOpen ? "bg-gray-100 dark:bg-white/[0.03]" : ""
            }`}
            onClick={handleToggle}
            aria-label="Toggle Sidebar"
          >
            {isMobileOpen ? (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M6.21967 7.28131C5.92678 6.98841 5.92678 6.51354 6.21967 6.22065C6.51256 5.92775 6.98744 5.92775 7.28033 6.22065L11.999 10.9393L16.7176 6.22078C17.0105 5.92789 17.4854 5.92788 17.7782 6.22078C18.0711 6.51367 18.0711 6.98855 17.7782 7.28144L13.0597 12L17.7782 16.7186C18.0711 17.0115 18.0711 17.4863 17.7782 17.7792C17.4854 18.0721 17.0105 18.0721 16.7176 17.7792L11.999 13.0607L7.28033 17.7794C6.98744 18.0722 6.51256 18.0722 6.21967 17.7794C5.92678 17.4865 5.92678 17.0116 6.21967 16.7187L10.9384 12L6.21967 7.28131Z"
                  fill="currentColor"
                />
              </svg>
            ) : (
              <svg
                width="16"
                height="12"
                viewBox="0 0 16 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M0.583252 1C0.583252 0.585788 0.919038 0.25 1.33325 0.25H14.6666C15.0808 0.25 15.4166 0.585786 15.4166 1C15.4166 1.41421 15.0808 1.75 14.6666 1.75L1.33325 1.75C0.919038 1.75 0.583252 1.41422 0.583252 1ZM0.583252 11C0.583252 10.5858 0.919038 10.25 1.33325 10.25L14.6666 10.25C15.0808 10.25 15.4166 10.5858 15.4166 11C15.4166 11.4142 15.0808 11.75 14.6666 11.75L1.33325 11.75C0.919038 11.75 0.583252 11.4142 0.583252 11ZM1.33325 5.25C0.919038 5.25 0.583252 5.58579 0.583252 6C0.583252 6.41421 0.919038 6.75 1.33325 6.75L7.99992 6.75C8.41413 6.75 8.74992 6.41421 8.74992 6C8.74992 5.58579 8.41413 5.25 7.99992 5.25L1.33325 5.25Z"
                  fill="currentColor"
                />
              </svg>
            )}
            {/* Cross Icon */}
          </button>

          <Link to="/" className="xl:hidden">
            <span className="text-lg font-bold text-gray-800 dark:text-white/90">
              {APP_NAME}
            </span>
          </Link>

          {/* [내부용 | 외부용] 탭은 2026-08-28 에 띠에서 뺐다(EditionTabs 컴포넌트도 함께 삭제).
              얼굴을 가르는 장치 자체(EditionContext·shared/external-edition)는 그대로다 —
              외부 배포용 계정은 로그인 신분으로 판정되므로 탭이 없어도 자기 화면을 그대로 받는다.
              ⚠️ 대신 **관리자가 미리보기로 넘어갈 손잡이가 화면에 없다** — 주소에 `?edition=external`
              을 붙여 들어가고, 돌아올 땐 그 표를 지운다. 미리보기 중임을 알리는 표시도 없으니
              탭을 되살리기 전엔 이 사실을 아는 사람만 쓰는 길이다. */}

          {/* 만나이(상시 노출) + 디데이 계산기(칩) — 전 라우트 공통. 좁은 화면에서는 숨긴다.
              2026-08-31: **선택 이용(외부용) 화면에도 그대로 둔다.** 우리 업무 데이터를 한 톨도
              읽지 않는 순수 계산 도구라 가릴 이유가 없었고(가리는 판단은 «새는 것»에만 쓴다),
              상담 중 만나이를 치는 일은 어느 매장이나 똑같이 생긴다. */}
          <HeaderDateCalc className="hidden xl:flex" />

          {/* 대표 전용 '오늘 할 일' — 2026-08-28 부터 띠 아래 노란 줄이 아니라 띠 안의 노란 칩이다.
              할 일이 없으면 아예 안 그려지므로 평소엔 폭을 안 먹는다. */}
          {!external && <RecurringTaskBanner className="hidden xl:block" />}

          {/* 전 화면 공통 통합검색 — **평소엔 짧게(14rem), 누르면 남는 폭을 다 쓴다.**
              예전엔 접힘도 flex-1 이라 늘 띠의 절반을 먹는 큰 빈 칸처럼 보였다(2026-08-28 지적).

              ⚠️ 접힘을 여기서 더 조이지 말 것 — 한때 돋보기 한 칸(36px)까지 줄였다가
              "뭔지 알아볼 수 없다"고 되돌렸다(2026-08-12). 14rem 은 '자료 검색' 넉 자가
              읽히는 최소선이다.

              한 줄 계산(1440 기준, 띠 가용폭 1102px): 예전엔 계산기 572 + 오른쪽 버튼 410 이라
              검색에 60px 밖에 안 남아 오른쪽 묶음이 아랫줄로 밀렸다. 2026-08-28 에 계산기를
              칩(약 150)으로 접고 [내부용|외부용] 탭(약 120)을 빼면서 고정 요소가 ~740 으로
              줄었다 — 1280 에서도 한 줄이다. 다시 두 줄이 되면 폭을 조이지 말고
              **새로 늘어난 요소**를 의심할 것. */}
          {external ? (
            // 검색칸이 차지하던 자리를 그대로 비워 둔다 — 오른쪽 묶음(계정)이 왼쪽으로
            // 딸려 오면 띠가 통째로 다른 화면처럼 보인다.
            <div className="flex-1" />
          ) : (
            <GlobalSearchBar
              onExpandedChange={setSearchExpanded}
              className={`min-w-0 transition-all duration-200 ease-out ${
                // 좁은 화면(모바일)에서는 접혀 있어도 넓게 둔다 — 현장에서 휴대폰으로 자료를
                // 여는 게 이 기능의 주 사용처이고, 거기선 옆에 자리를 다툴 요소도 없다.
                isSearchExpanded
                  ? "w-full flex-1"
                  // ⚠️ 접힘도 flex-1(기준폭 0)로 두고 **max-w 로만 짧게 만든다.** 고정폭(w-56)을
                  // 주면 브라우저가 줄바꿈을 계산할 때 그 224px 을 통째로 셈해, 실제로는 남는
                  // 자리가 있는데도 오른쪽 묶음을 아랫줄로 내려보낸다(2026-08-28 실측).
                  : "w-full flex-1 xl:max-w-[11rem] 2xl:max-w-[16rem]"
              }`}
            />
          )}

          {/* 개인정보 가리기 (티켓 #461) — 띠에는 **'가리기 해제'만** 선다.
              평소의 '가리기'는 2026-08-28 에 왼쪽 메뉴 ⚙️ 설정으로 옮겼다(띠에 늘 떠 있던
              눈 아이콘이 뜬금없다는 지적). 해제 버튼은 지금 개인정보가 드러나 있다는
              경고를 겸하므로 여기 남는다. */}
          {!external && <PrivacyUnlockButton place="header" />}

          {/* 이미지에서 글자 뽑기 — 검색칸 오른쪽, 전 화면·전 직원 공통(티켓 #436).
              2026-08-28: 검색을 펼칠 때 감추던 걸 그만뒀다. 계산기가 칩으로 접히면서 자리가
              남아 감출 이유가 없어졌고, 검색할 때마다 옆 버튼이 사라졌다 나타나는 게 더 어수선
              했다. 좁은 화면에선 아이콘만. */}
          {!external && <ImageTextExtractor />}

          <button
            onClick={toggleApplicationMenu}
            className="flex items-center justify-center w-10 h-10 text-gray-700 rounded-lg z-99999 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 xl:hidden"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.99902 10.4951C6.82745 10.4951 7.49902 11.1667 7.49902 11.9951V12.0051C7.49902 12.8335 6.82745 13.5051 5.99902 13.5051C5.1706 13.5051 4.49902 12.8335 4.49902 12.0051V11.9951C4.49902 11.1667 5.1706 10.4951 5.99902 10.4951ZM17.999 10.4951C18.8275 10.4951 19.499 11.1667 19.499 11.9951V12.0051C19.499 12.8335 18.8275 13.5051 17.999 13.5051C17.1706 13.5051 16.499 12.8335 16.499 12.0051V11.9951C16.499 11.1667 17.1706 10.4951 17.999 10.4951ZM13.499 11.9951C13.499 11.1667 12.8275 10.4951 11.999 10.4951C11.1706 10.4951 10.499 11.1667 10.499 11.9951V12.0051C10.499 12.8335 11.1706 13.5051 11.999 13.5051C12.8275 13.5051 13.499 12.8335 13.499 12.0051V11.9951Z"
                fill="currentColor"
              />
            </svg>
          </button>

        </div>
        <div
          className={`${
            // 2026-08-28: 검색을 펼칠 때 이 묶음(QR·서명·폼·계정)을 통째로 감추던 규칙을 뺐다.
            // 자리가 남게 됐고, 검색 한 번에 헤더가 딴 화면처럼 바뀌는 게 더 불편했다.
            isApplicationMenuOpen ? "flex xl:flex" : "hidden xl:flex"
          } items-center justify-between w-full gap-4 px-5 py-4 shadow-theme-md xl:gap-3 xl:ml-auto xl:w-auto xl:shrink-0 xl:justify-end xl:px-0 xl:shadow-none`}
        >
          <div className="flex shrink-0 items-center gap-2 2xsm:gap-3 xl:gap-2">
            {/* 새 버전 「업데이트」 — 새 버전이 나왔을 때만 선다(안 눌러도 다른 화면으로 옮기면 저절로 바뀐다).
                외부용 화면에도 보인다(앱을 계속 쓰려면 필요한 최소한 — 옛 코드로 새 서버를 치면 터진다). */}
            <NewBuildHeaderButton />
            {isCrmSection && (
              <KioskQrButton className="h-9 shrink-0 whitespace-nowrap px-2.5 rounded-lg border border-brand-500 text-brand-500 text-xs font-semibold hover:bg-brand-50 dark:hover:bg-white/[0.03] transition flex items-center gap-1.5 disabled:opacity-60" />
            )}
            {isCrmSection && (
              <SignaturePadButton className="h-9 shrink-0 whitespace-nowrap border border-brand-500 px-2.5 text-xs font-semibold text-brand-500 transition hover:bg-brand-50 dark:hover:bg-white/[0.03]" />
            )}
            {/* 폼링크 생성 + 폼 바로 열기 — 같은 기능의 두 갈래라 한 칸으로 합쳤다(2026-08-28). */}
            {isCrmSection && <FormLinkSplitButton />}
            {/* <!-- Dark Mode Toggler --> */}
            <ThemeToggleButton />
            {/* <!-- Dark Mode Toggler --> */}
            {/* '업데이트 소식' — 2026-08-12 에 "안 쓰는 기능"이라며 뺐다가 되살렸다.
                안 쓰인 이유가 기능이 없어서가 아니라 **볼 게 없어서**였다: 소식에 요청 원문이
                안 실려 있었고, 지난 소식을 훑을 화면도 없었다. 둘 다 채웠으니 다시 문을 연다.
                (이 단추가 없는 동안 배포 알림의 「확인하기」도 같이 죽어 있었다 — 창을 그리던
                코드가 이 안에 있었기 때문. 지금 창은 AppLayout 에 따로 산다.) */}
            {!external && <WhatsNewButton />}
          </div>
          {/* <!-- User Area --> */}
          <UserDropdown />
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
